import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Building2, MapPin, Phone, Globe, Clock, Mail, ExternalLink,
  ChevronLeft, ChevronRight, Share2, Facebook, Instagram, Plus,
  ArrowLeft, MessageCircle, Copy, Check, ImageIcon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/components/ui/carousel";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import Layout from "@/components/layout/Layout";
import { AdBanner } from "@/components/shared/AdPlaceholder";
import axios from "axios";
import { API } from "@/App";

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  if (url.startsWith("/uploads/")) {
    return `${backendUrl}/api${url}`;
  }
  return `${backendUrl}${url}`;
};

const categoryIcons = {
  food: "Comida y Restaurantes",
  tourism: "Turismo y Hoteles",
  health: "Salud",
  services: "Servicios Profesionales",
  transport: "Transporte",
  shops: "Tiendas",
  education: "Educación",
  entertainment: "Entretenimiento",
  beauty: "Belleza y Estética",
  automotive: "Automotriz",
  "real-estate": "Bienes Raíces",
  other: "Otros",
};

export default function BusinessDetailPage() {
  const { slug } = useParams();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    fetchBusiness();
  }, [slug]);

  const fetchBusiness = async () => {
    try {
      const response = await axios.get(`${API}/public/business/${slug}`);
      setBusiness(response.data);
      // Track business view
      if (response.data?.business_id) {
        axios.post(`${API}/track-view/business/${response.data.business_id}`).catch(() => {});
      }
    } catch (error) {
      console.error("Error fetching business:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    toast.success("Enlace copiado al portapapeles");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = (platform) => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(`Conoce ${business?.name} en Choluteca.net`);

    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      whatsapp: `https://wa.me/?text=${text}%20${url}`,
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
    };

    window.open(shareUrls[platform], "_blank", "width=600,height=400");
  };

  // Build all images array
  const getAllImages = () => {
    const images = [];
    if (business?.featured_image_url) {
      images.push({ url: business.featured_image_url, isFeatured: true });
    }
    if (business?.gallery_urls && business.gallery_urls.length > 0) {
      business.gallery_urls.forEach((img) => {
        images.push({
          url: typeof img === "string" ? img : img.url,
          isFeatured: false,
        });
      });
    }
    return images;
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  if (!business) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
          <Building2 className="h-20 w-20 text-gray-300 mb-6" />
          <h1 className="text-2xl font-bold text-gray-700 mb-2">
            Empresa no encontrada
          </h1>
          <p className="text-gray-500 mb-6">
            El negocio que buscas no existe o ha sido removido.
          </p>
          <Link to="/directorio">
            <Button>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver al Directorio
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const allImages = getAllImages();
  const categoryName = categoryIcons[business.category] || "Otros";

  return (
    <Layout>
      {/* Hero Section with Image Gallery */}
      <section className="relative bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900">
        {/* Back Button */}
        <div className="absolute top-4 left-4 z-20">
          <Link to="/directorio">
            <Button
              variant="ghost"
              className="bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Directorio
            </Button>
          </Link>
        </div>

        {/* Share Button */}
        <div className="absolute top-4 right-4 z-20">
          <Button
            onClick={() => setShareDialogOpen(true)}
            className="bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm"
          >
            <Share2 className="h-4 w-4 mr-2" />
            Compartir
          </Button>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Image Gallery */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="relative"
            >
              {allImages.length > 0 ? (
                <div className="relative">
                  {allImages.length === 1 ? (
                    <div
                      className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl cursor-pointer"
                      onClick={() => {
                        setLightboxIndex(0);
                        setLightboxOpen(true);
                      }}
                    >
                      <img
                        src={getImageUrl(allImages[0].url)}
                        alt={business.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <Carousel className="w-full" opts={{ loop: true }}>
                      <CarouselContent>
                        {allImages.map((img, index) => (
                          <CarouselItem key={index}>
                            <div
                              className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl cursor-pointer"
                              onClick={() => {
                                setLightboxIndex(index);
                                setLightboxOpen(true);
                              }}
                            >
                              <img
                                src={getImageUrl(img.url)}
                                alt={`${business.name} - ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                      <CarouselPrevious className="left-3 bg-white/90 hover:bg-white shadow-lg" />
                      <CarouselNext className="right-3 bg-white/90 hover:bg-white shadow-lg" />
                    </Carousel>
                  )}
                  {/* Photo Counter */}
                  {allImages.length > 1 && (
                    <div className="absolute bottom-4 right-4 bg-black/60 text-white text-sm px-3 py-1.5 rounded-full flex items-center gap-1.5 backdrop-blur-sm">
                      <ImageIcon className="h-4 w-4" />
                      {allImages.length} fotos
                    </div>
                  )}
                </div>
              ) : (
                <div className="aspect-[4/3] rounded-2xl bg-white/10 flex items-center justify-center">
                  <Building2 className="h-20 w-20 text-white/30" />
                </div>
              )}
            </motion.div>

            {/* Business Info Header */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-white"
            >
              <Badge className="bg-white/20 text-white border-0 mb-4">
                {categoryName}
              </Badge>
              <h1 className="text-4xl sm:text-5xl font-bold mb-4">
                {business.name}
              </h1>
              {business.description && (
                <p className="text-lg text-white/80 mb-6 leading-relaxed">
                  {business.description}
                </p>
              )}

              {/* Quick Contact Buttons */}
              <div className="flex flex-wrap gap-3">
                {business.phone && (
                  <a href={`tel:${business.phone}`}>
                    <Button className="bg-white text-emerald-800 hover:bg-white/90">
                      <Phone className="h-4 w-4 mr-2" />
                      Llamar
                    </Button>
                  </a>
                )}
                {business.whatsapp && (
                  <a
                    href={`https://wa.me/${business.whatsapp.replace(/\D/g, "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button className="bg-green-500 hover:bg-green-600 text-white">
                      <MessageCircle className="h-4 w-4 mr-2" />
                      WhatsApp
                    </Button>
                  </a>
                )}
                {business.website && (
                  <a
                    href={business.website}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button
                      variant="outline"
                      className="border-white/30 text-white hover:bg-white/10"
                    >
                      <Globe className="h-4 w-4 mr-2" />
                      Sitio Web
                    </Button>
                  </a>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Services */}
              {business.services && business.services.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-2xl p-6 shadow-sm"
                >
                  <h2 className="text-xl font-bold text-gray-800 mb-4">
                    Servicios
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {business.services.map((service, i) => (
                      <Badge
                        key={i}
                        variant="secondary"
                        className="bg-emerald-50 text-emerald-700 px-4 py-2 text-sm"
                      >
                        {service}
                      </Badge>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Photo Gallery Thumbnails */}
              {allImages.length > 1 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white rounded-2xl p-6 shadow-sm"
                >
                  <h2 className="text-xl font-bold text-gray-800 mb-4">
                    Galería de Fotos
                  </h2>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {allImages.map((img, index) => (
                      <div
                        key={index}
                        className="aspect-square rounded-xl overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => {
                          setLightboxIndex(index);
                          setLightboxOpen(true);
                        }}
                      >
                        <img
                          src={getImageUrl(img.url)}
                          alt={`${business.name} - ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Ad Banner */}
              <AdBanner />
            </div>

            {/* Right Column - Contact Info */}
            <div className="space-y-6">
              {/* Contact Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-2xl p-6 shadow-sm sticky top-24"
              >
                <h2 className="text-xl font-bold text-gray-800 mb-4">
                  Información de Contacto
                </h2>
                <div className="space-y-4">
                  {business.address && (
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <MapPin className="h-5 w-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Dirección</p>
                        <p className="font-medium text-gray-800">
                          {business.address}
                        </p>
                        {business.municipio && (
                          <p className="text-sm text-gray-600">
                            {business.municipio}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {business.phone && (
                    <a
                      href={`tel:${business.phone}`}
                      className="flex items-start gap-3 group"
                    >
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Phone className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Teléfono</p>
                        <p className="font-medium text-blue-600 group-hover:underline">
                          {business.phone}
                        </p>
                      </div>
                    </a>
                  )}

                  {business.whatsapp && (
                    <a
                      href={`https://wa.me/${business.whatsapp.replace(/\D/g, "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-3 group"
                    >
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <MessageCircle className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">WhatsApp</p>
                        <p className="font-medium text-green-600 group-hover:underline">
                          {business.whatsapp}
                        </p>
                      </div>
                    </a>
                  )}

                  {business.email && (
                    <a
                      href={`mailto:${business.email}`}
                      className="flex items-start gap-3 group"
                    >
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                        <Mail className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="font-medium text-purple-600 group-hover:underline">
                          {business.email}
                        </p>
                      </div>
                    </a>
                  )}

                  {business.hours && (
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <Clock className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Horario</p>
                        <p className="font-medium text-gray-800">
                          {business.hours}
                        </p>
                      </div>
                    </div>
                  )}

                  {business.website && (
                    <a
                      href={business.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-3 group"
                    >
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <Globe className="h-5 w-5 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Sitio Web</p>
                        <p className="font-medium text-gray-600 group-hover:underline flex items-center gap-1">
                          Visitar sitio
                          <ExternalLink className="h-4 w-4" />
                        </p>
                      </div>
                    </a>
                  )}
                </div>

                {/* Social Links */}
                {(business.facebook || business.instagram || business.tiktok) && (
                  <div className="mt-6 pt-6 border-t">
                    <p className="text-sm text-gray-500 mb-3">Redes Sociales</p>
                    <div className="flex gap-3">
                      {business.facebook && (
                        <a
                          href={business.facebook}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white hover:bg-blue-700 transition-colors"
                        >
                          <Facebook className="h-5 w-5" />
                        </a>
                      )}
                      {business.instagram && (
                        <a
                          href={business.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-pink-500 flex items-center justify-center text-white hover:opacity-90 transition-opacity"
                        >
                          <Instagram className="h-5 w-5" />
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Share Button */}
                <Button
                  onClick={() => setShareDialogOpen(true)}
                  variant="outline"
                  className="w-full mt-6"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Compartir este negocio
                </Button>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Add Your Business */}
      <section className="py-16 bg-gradient-to-br from-emerald-600 to-teal-700">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              ¿Tienes un negocio en Choluteca?
            </h2>
            <p className="text-lg text-white/80 mb-8">
              Registra tu empresa en nuestro directorio y llega a miles de
              clientes potenciales. Es gratis y fácil.
            </p>
            <Link to="/directorio">
              <Button
                size="lg"
                className="bg-white text-emerald-700 hover:bg-white/90 text-lg px-8 py-6 rounded-full shadow-lg"
              >
                <Plus className="h-5 w-5 mr-2" />
                Agregar mi empresa gratis
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Compartir {business?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Copy Link */}
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={typeof window !== "undefined" ? window.location.href : ""}
                className="flex-1 px-3 py-2 border rounded-lg text-sm bg-gray-50"
              />
              <Button onClick={handleCopyLink} variant="outline">
                {copied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>

            {/* Social Share Buttons */}
            <div className="flex justify-center gap-4">
              <button
                onClick={() => handleShare("facebook")}
                className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white hover:bg-blue-700 transition-colors"
              >
                <Facebook className="h-6 w-6" />
              </button>
              <button
                onClick={() => handleShare("whatsapp")}
                className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center text-white hover:bg-green-600 transition-colors"
              >
                <MessageCircle className="h-6 w-6" />
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-4xl bg-black/95 border-0 p-2">
          <div className="relative">
            <img
              src={getImageUrl(allImages[lightboxIndex]?.url)}
              alt={`${business?.name} - ${lightboxIndex + 1}`}
              className="w-full max-h-[80vh] object-contain"
            />
            {allImages.length > 1 && (
              <>
                <button
                  onClick={() =>
                    setLightboxIndex(
                      (lightboxIndex - 1 + allImages.length) % allImages.length
                    )
                  }
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  onClick={() =>
                    setLightboxIndex((lightboxIndex + 1) % allImages.length)
                  }
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white text-sm px-3 py-1 rounded-full">
                  {lightboxIndex + 1} / {allImages.length}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
